/**
 * Mock Absence Data Generator
 * 
 * Generates realistic absence entries from HR system integration
 * These entries are read-only and automatically coded as non-project time
 */

import type { TimesheetEntry } from '@/types';

export type AbsenceType = 
  | 'annual-leave'
  | 'sick-leave'
  | 'parental-leave'
  | 'compassionate-leave'
  | 'public-holiday'
  | 'unpaid-leave'
  | 'study-leave';

export interface AbsenceEntry extends TimesheetEntry {
  // Absence-specific fields
  isAbsence: true;
  absenceType: AbsenceType;
  absenceReason?: string;
  approvedBy?: string;
  approvedAt?: Date;
  absenceId?: string; // ID from HR system (e.g., 'ABS-2026-001234')
  absenceRequestId?: string; // Legacy field, use absenceId instead
  isReadOnly: true;
  isNonProjectTime: true;
  sourceIntegration: 'people-hr';
}

export interface AbsenceSettings {
  projectId: string;
  costCentreId: string;
}

/**
 * Map cost centre to absence type
 * In production, the cost centre automatically determines the absence type
 */
export function getAbsenceTypeFromCostCentre(costCentreId: string): AbsenceType {
  // Map cost centre IDs to absence types
  const costCentreMapping: Record<string, AbsenceType> = {
    'cc-training': 'study-leave', // Training cost centre maps to study leave
    'cc-annual': 'annual-leave',
    'cc-sick': 'sick-leave',
    'cc-parental': 'parental-leave',
    'cc-compassionate': 'compassionate-leave',
    'cc-public-holiday': 'public-holiday',
    'cc-unpaid': 'unpaid-leave',
    'cc-study': 'study-leave',
  };

  // Return mapped type or default to annual leave
  return costCentreMapping[costCentreId] || 'annual-leave';
}

/**
 * Get absence settings from localStorage
 * Uses the same storage key as PeopleHRSettings component
 */
export function getAbsenceSettings(): AbsenceSettings | null {
  console.log('[getAbsenceSettings] Attempting to load settings from localStorage');
  const settings = localStorage.getItem('peopleHR_absence_settings');
  console.log('[getAbsenceSettings] Raw settings from localStorage:', settings);
  
  if (!settings) {
    console.log('[getAbsenceSettings] No settings found in localStorage');
    return null;
  }
  
  try {
    const parsed = JSON.parse(settings);
    console.log('[getAbsenceSettings] Parsed settings:', parsed);
    
    // Extract just the IDs we need
    if (parsed.projectId && parsed.costCentreId) {
      const result = {
        projectId: parsed.projectId,
        costCentreId: parsed.costCentreId,
      };
      console.log('[getAbsenceSettings] Returning settings:', result);
      return result;
    }
    console.log('[getAbsenceSettings] Settings missing projectId or costCentreId');
    return null;
  } catch (error) {
    console.error('[getAbsenceSettings] Error parsing settings:', error);
    return null;
  }
}

/**
 * Save absence settings to localStorage
 * Uses the same storage key as PeopleHRSettings component
 */
export function saveAbsenceSettings(settings: AbsenceSettings): void {
  localStorage.setItem('peopleHR_absence_settings', JSON.stringify(settings));
}

/**
 * Get absence type display name
 */
export function getAbsenceTypeLabel(type: AbsenceType): string {
  const labels: Record<AbsenceType, string> = {
    'annual-leave': 'Annual Leave',
    'sick-leave': 'Sick Leave',
    'parental-leave': 'Parental Leave',
    'compassionate-leave': 'Compassionate Leave',
    'public-holiday': 'Public Holiday',
    'unpaid-leave': 'Unpaid Leave',
    'study-leave': 'Study Leave',
  };
  return labels[type];
}

/**
 * Get absence type color for badges
 */
export function getAbsenceTypeColor(type: AbsenceType): string {
  const colors: Record<AbsenceType, string> = {
    'annual-leave': 'bg-info-100 text-info-800 border-info-200',
    'sick-leave': 'bg-error-100 text-error-800 border-error-200',
    'parental-leave': 'bg-purple-100 text-purple-800 border-purple-200',
    'compassionate-leave': 'bg-neutral-100 text-neutral-800 border-neutral-200',
    'public-holiday': 'bg-burntorange-100 text-burntorange-800 border-burntorange-200',
    'unpaid-leave': 'bg-warning-100 text-warning-800 border-warning-200',
    'study-leave': 'bg-blueviolet-100 text-blueviolet-800 border-blueviolet-200',
  };
  return colors[type];
}

/**
 * Generate mock absence entries for a specific week
 * @param employeeId - The employee ID
 * @param weekEnding - The week ending date
 * @param settings - Absence settings (project and cost centre)
 * @returns Array of absence entries
 */
export function generateMockAbsenceEntries(
  employeeId: number,
  weekEnding: Date,
  settings: AbsenceSettings | null
): AbsenceEntry[] {
  // If no settings configured, return empty array
  if (!settings) {
    console.log('[Absence] No settings configured, skipping absence generation');
    return [];
  }

  console.log('[Absence] Generating absences for:', { employeeId, weekEnding, settings });

  const entries: AbsenceEntry[] = [];
  const weekStart = new Date(weekEnding);
  weekStart.setDate(weekStart.getDate() - 6); // Go back 6 days to get Monday

    // Generate realistic absence patterns based on the week
    const weekNumber = getWeekNumber(weekEnding);
    const seed = employeeId + weekNumber;

  // Randomly decide if this week has any absences (30% chance)
  // If seed % 10 >= 7, skip this week (30% chance of NO absence)
  if (seed % 10 >= 7) {
    console.log('[Absence] Skipping week (no absence this week)');
    return entries;
  }

  console.log('[Absence] Week will have absence, seed:', seed);

  // Determine absence type from cost centre (production behavior)
  // The cost centre automatically categorizes the absence type
  const absenceType = getAbsenceTypeFromCostCentre(settings.costCentreId);
  console.log('[Absence] Absence type determined from cost centre:', { costCentreId: settings.costCentreId, absenceType });

  // Generate absence pattern
  const pattern = generateAbsencePattern(absenceType, seed);

  if (pattern.totalHours > 0) {
    const entry: AbsenceEntry = {
      id: -Date.now() - Math.floor(Math.random() * 1000),
      projectId: settings.projectId,
      costCentreId: settings.costCentreId,
      description: generateAbsenceDescription(absenceType),
      hours: pattern.hours,
      // Absence-specific fields
      isAbsence: true,
      absenceType,
      absenceReason: generateAbsenceReason(absenceType),
      approvedBy: 'HR Department',
      approvedAt: new Date(weekStart.getTime() - 7 * 24 * 60 * 60 * 1000), // Approved a week before
      absenceRequestId: `ABS-${seed}-${weekNumber}`,
      isReadOnly: true,
      isNonProjectTime: true,
      sourceIntegration: 'people-hr',
    };

    entries.push(entry);
    console.log('[Absence] Generated absence entry:', entry);
  }

  console.log('[Absence] Returning', entries.length, 'absence entries');
  return entries;
}

/**
 * Generate absence pattern (which days and how many hours)
 */
function generateAbsencePattern(
  type: AbsenceType,
  seed: number
): {
  hours: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  };
  totalHours: number;
} {
  const hours = {
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
  };

  // Different patterns for different absence types
  switch (type) {
    case 'annual-leave':
      // Full week or partial week
      if (seed % 3 === 0) {
        // Full week
        hours.monday = 8;
        hours.tuesday = 8;
        hours.wednesday = 8;
        hours.thursday = 8;
        hours.friday = 8;
      } else if (seed % 3 === 1) {
        // First 3 days
        hours.monday = 8;
        hours.tuesday = 8;
        hours.wednesday = 8;
      } else {
        // Last 2 days
        hours.thursday = 8;
        hours.friday = 8;
      }
      break;

    case 'sick-leave':
      // Usually 1-3 days
      const sickDays = (seed % 3) + 1;
      if (sickDays === 1) {
        hours.monday = 8;
      } else if (sickDays === 2) {
        hours.monday = 8;
        hours.tuesday = 8;
      } else {
        hours.monday = 8;
        hours.tuesday = 8;
        hours.wednesday = 8;
      }
      break;

    case 'public-holiday':
      // Single day
      hours.monday = 8;
      break;

    case 'parental-leave':
      // Full week
      hours.monday = 8;
      hours.tuesday = 8;
      hours.wednesday = 8;
      hours.thursday = 8;
      hours.friday = 8;
      break;

    case 'compassionate-leave':
      // 1-3 days
      hours.monday = 8;
      hours.tuesday = 8;
      break;

    case 'unpaid-leave':
      // Full week
      hours.monday = 8;
      hours.tuesday = 8;
      hours.wednesday = 8;
      hours.thursday = 8;
      hours.friday = 8;
      break;

    case 'study-leave':
      // 1-2 days
      hours.thursday = 8;
      hours.friday = 8;
      break;
  }

  const totalHours =
    hours.monday +
    hours.tuesday +
    hours.wednesday +
    hours.thursday +
    hours.friday +
    hours.saturday +
    hours.sunday;

  return { hours, totalHours };
}

/**
 * Generate absence description
 */
function generateAbsenceDescription(type: AbsenceType): string {
  const descriptions: Record<AbsenceType, string> = {
    'annual-leave': 'Annual Leave',
    'sick-leave': 'Sick Leave',
    'parental-leave': 'Parental Leave',
    'public-holiday': 'Bank Holiday',
    'compassionate-leave': 'Compassionate Leave',
    'unpaid-leave': 'Unpaid Leave',
    'study-leave': 'Study Leave',
  };
  return descriptions[type];
}

/**
 * Generate absence reason
 */
function generateAbsenceReason(type: AbsenceType): string {
  const reasons: Record<AbsenceType, string> = {
    'annual-leave': 'Pre-approved annual leave',
    'sick-leave': 'Medical absence',
    'parental-leave': 'Parental leave entitlement',
    'public-holiday': 'Statutory bank holiday',
    'compassionate-leave': 'Family emergency',
    'unpaid-leave': 'Personal leave',
    'study-leave': 'Educational course',
  };
  return reasons[type];
}

/**
 * Get week number from date
 */
function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

/**
 * Check if an entry is an absence entry
 */
export function isAbsenceEntry(entry: TimesheetEntry): entry is AbsenceEntry {
  return 'isAbsence' in entry && entry.isAbsence === true;
}

/**
 * Get total absence hours for a week
 */
export function getTotalAbsenceHours(entries: TimesheetEntry[]): number {
  return entries
    .filter(isAbsenceEntry)
    .reduce((total, entry) => {
      return (
        total +
        entry.hours.monday +
        entry.hours.tuesday +
        entry.hours.wednesday +
        entry.hours.thursday +
        entry.hours.friday +
        entry.hours.saturday +
        entry.hours.sunday
      );
    }, 0);
}

/**
 * Get absence entries from a list of entries
 */
export function getAbsenceEntries(entries: TimesheetEntry[]): AbsenceEntry[] {
  return entries.filter(isAbsenceEntry);
}

/**
 * Get non-absence entries from a list of entries
 */
export function getNonAbsenceEntries(entries: TimesheetEntry[]): TimesheetEntry[] {
  return entries.filter(entry => !isAbsenceEntry(entry));
}
